import { OmakaseTextColorsWithInherit } from 'theme/typography/omakaseTypography';
import { PlainButton, Typography } from 'omakase';
import { Theme, css } from '@emotion/react';
import { buttonStyle } from './expandableParagraphCssDefs';
import DOMPurify from 'isomorphic-dompurify';
import NewlineEscapedText from '@tock/shared/components/NewlineEscapedText';
import React, { FunctionComponent } from 'react';
import ellipsis from 'html-ellipsis';

export const DEFAULT_PARAGRAPH_LENGTH_CUTOFF = 102;
export type ExpandableParagraphVariant = 'body01' | 'body02' | 'caption';

export interface ExpandableParagraphProps {
  /**
   * The typography variant
   */
  variant?: ExpandableParagraphVariant;
  /**
   * Color of the text, based on theme text colors. Defaults to fgDefault.
   */
  color?: OmakaseTextColorsWithInherit;
  /**
   * The text of the paragraph on display, if length < cutoffLength, 'More' / 'Hide' will not be displayed
   */
  paragraphText?: string;
  /**
   * The length at which to truncate paragraphText, default value is 102
   */
  cutoffLength?: number;
  /**
   * Whether initial view is expanded or collapsed, default is collapsed
   */
  isInitiallyExpanded?: boolean;
  className?: string;
  mb?: number;
  linkify?: boolean;
}

export const ExpandableParagraph: FunctionComponent<ExpandableParagraphProps> = ({
  variant = 'body02',
  color = 'fgDefault',
  paragraphText,
  isInitiallyExpanded = false,
  cutoffLength = DEFAULT_PARAGRAPH_LENGTH_CUTOFF,
  mb = 0,
  linkify,
  ...rest
}) => {
  const [isExpanded, setIsExpanded] = React.useState(isInitiallyExpanded);

  if (!paragraphText) {
    return null;
  }

  const isParagraphShort = paragraphText.length < cutoffLength;
  const cleanParagraphText = DOMPurify.sanitize(paragraphText);

  const finalParagraphText =
    isParagraphShort || isExpanded
      ? cleanParagraphText
      : `${ellipsis(cleanParagraphText, cutoffLength, false)}...`;
  const label = isExpanded ? 'Hide' : 'More';

  const getPlainButtonSize = (variant: ExpandableParagraphVariant) => {
    switch (variant) {
      case 'body01':
        return 'xlarge';
      case 'body02':
        return 'medium';
      case 'caption':
        return 'xsmall';
      default:
        return 'medium';
    }
  };

  return (
    <div {...rest}>
      <p
        css={({ spacing }: Theme) => css`
          margin-bottom: ${spacing(mb)}px;
        `}
      >
        <Typography variant={variant} color={color} component="span">
          <NewlineEscapedText text={finalParagraphText} linkify={linkify} />
        </Typography>

        {!isParagraphShort && (
          <PlainButton
            variant="primary"
            size={getPlainButtonSize(variant)}
            aria-label={label}
            onClick={() => setIsExpanded((currentValue) => !currentValue)}
            tabIndex={0}
            css={buttonStyle}
            type="button"
          >
            <span>{label}</span>
          </PlainButton>
        )}
      </p>
    </div>
  );
};

export default ExpandableParagraph;
